import {Component, PanelLifecycleContext} from 'panel';
import type {
  CopilotCardContext,
  CopilotCurrentPageContext,
  CopilotProfileContext,
} from '@mixpanel/ai-copilot/agent/types';
import pick from 'lodash/pick';

import {Subscribable} from 'common/types/context/subscribable';
import {BaseQueryErrorType, BaseQueryResultsType} from 'common/report/types';
import {Annotation} from 'common/report/model-resources/annotations/types';
import {BookmarkParamsUnionType} from 'common/report/dashboards/types';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import type {ReportBookmarkManager} from 'common/report/insights/contexts/report-bookmark-manager';
import type {FilterEntry, SortOption} from 'common/widgets/recording-player/types';
import type {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import type {Playlist} from 'common/widgets/playlists/types';

export interface SessionReplayFiltersUpdate {
  eventFilters?: FilterEntry[];
  dateRange?: DateRangeAttributes;
  sortOption?: SortOption;
}

export interface PlaylistCardData {
  name: string;
  filters: Playlist[`filters`];
  replayIds: string[];
}

export interface ReplayPlaybackRequest {
  replayId: string;
  eventTime: number; // absolute unix seconds
}

export interface ReplayDisplayMetadata {
  time: number; // unix seconds (replay start)
  distinctId: string;
  name: string;
  email: string;
}

export type BoardEditedAction = `add` | `remove` | `update` | `updateBoard`;

export interface BoardEditedDetail {
  dashboardId: number;
  bookmarkId?: number;
  contentId?: number;
  action: BoardEditedAction;
  toolCallId: string;
}

export interface BoardSaveDetail {
  dashboardId: number;
  toolCallId: string;
}

// Internal card context with raw inputs for change detection (prevents infinite loops)
type InternalCopilotCardContext = CopilotCardContext & {
  _rawRequest?: BookmarkParamsUnionType;
  _rawResponse?: BaseQueryResultsType | BaseQueryErrorType;
};

// Internal board context using the extended card type
type InternalCopilotBoardContext = {
  dashboardId?: string | number;
  title?: string;
  description?: string;
  canEdit?: boolean;
  cards: Record<string, InternalCopilotCardContext>;
};

export class CopilotCurrentPageManager implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  private reportRequest: {params?: BookmarkParamsUnionType} | null = null;
  private reportResponse: BaseQueryResultsType | BaseQueryErrorType | null = null;
  private reportTitle: string | null = null;
  private reportDescription: string | null = null;
  private annotations: Annotation[] = [];
  private annotationsHash: string = ``;

  boardContext: InternalCopilotBoardContext | null = null;
  // The active report's bookmark manager. Apply-edit tools (e.g. `editMetric`) read
  // current params off this and call `applyParams` to push their changes back through
  // the report editor.
  public reportBookmark: ReportBookmarkManager | null = null;
  private profileDistinctId: string | null = null;
  private experimentCtx: CopilotCurrentPageContext[`experimentContext`] | null = null;
  private featureFlagCtx: CopilotCurrentPageContext[`featureFlagContext`] | null = null;

  // Session Replay handler — enables Copilot to update filters, date range, and sort on the SR page
  private sessionReplayHandler: ((update: SessionReplayFiltersUpdate) => void) | null = null;
  private pendingSessionReplayFiltersUpdate: SessionReplayFiltersUpdate | null = null;
  private sessionReplayCtx: CopilotCurrentPageContext[`sessionReplayContext`] | null = null;

  // Replay playback handler — enables Copilot to jump the SR page's player to a specific moment
  private replayPlaybackHandler: ((req: ReplayPlaybackRequest) => void) | null = null;

  // Client-only per-replay display metadata used by copilot tool render components.
  private replayDisplayMetadata: Map<string, ReplayDisplayMetadata> = new Map();

  // Board edited handler — enables the dashboards page to refresh after Copilot mutates a board.
  // The handler can be async; rejections are caught in notifyBoardEdited so a transient fetch
  // failure does not become an unhandled promise rejection.
  private boardEditedHandler: ((detail: BoardEditedDetail) => void | Promise<void>) | null = null;
  // Dedupes notifications by toolCallId so re-mounts of the tool result component don't re-fire
  private notifiedBoardEditedToolCallIds: Set<string> = new Set();

  // Board saving / saved handlers — let the dashboards page reflect saveBoardTool progress
  // (in-flight indicator while the agent finalizes a draft) and refresh once it succeeds.
  // Same async + dedupe semantics as boardEditedHandler.
  private boardSavingHandler: ((detail: BoardSaveDetail) => void | Promise<void>) | null = null;
  private boardSavedHandler: ((detail: BoardSaveDetail) => void | Promise<void>) | null = null;
  private notifiedBoardSavingToolCallIds: Set<string> = new Set();
  private notifiedBoardSavedToolCallIds: Set<string> = new Set();

  setReportRequest(request: {params?: BookmarkParamsUnionType; name?: string; description?: string}) {
    // Only notify if the params reference has actually changed
    if (
      this.reportRequest?.params === request?.params &&
      this.reportTitle === (request?.name ?? null) &&
      this.reportDescription === (request?.description ?? null)
    ) {
      return;
    }
    this.reportRequest = request;
    this.reportTitle = request?.name ?? null;
    this.reportDescription = request?.description ?? null;
    this.updateComponents();
  }

  // Stored separately from `setReportRequest` because `reportRequest` is the
  // serialized snapshot copilot sends to the LLM, while `reportBookmark` is a live
  // manager handle the FE uses to apply edits back to the page. Keeping them split
  // avoids coupling the LLM-context payload to FE-only state.
  setReportBookmark(bookmarkManager: ReportBookmarkManager | null) {
    if (this.reportBookmark === bookmarkManager) {
      return;
    }
    this.reportBookmark = bookmarkManager;
    this.updateComponents();
  }

  setReportResponse(response: BaseQueryResultsType | BaseQueryErrorType) {
    // Only notify if the response reference has actually changed
    if (this.reportResponse === response) {
      return;
    }
    this.reportResponse = response;
    this.updateComponents();
  }

  setAnnotations(annotations: Annotation[]) {
    // Create a simple hash from annotations to detect content changes
    // Why: the annotations array is a new reference every time due to getAllValues
    // and doing it this way is simpler than a deep comparison
    const newHash = `${annotations.length}:${annotations
      .sort((a) => a.id)
      .map((a) => a.id)
      .join(`,`)}`;

    // Only notify if the annotations content has actually changed
    if (this.annotationsHash === newHash) {
      return;
    }

    this.annotations = annotations;
    this.annotationsHash = newHash;
    this.updateComponents();
  }

  clearReportContext() {
    this.reportRequest = null;
    this.reportResponse = null;
    this.reportTitle = null;
    this.reportDescription = null;
    this.annotations = [];
    this.reportBookmark = null;

    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  hasReportContext(): boolean {
    return !!(this.reportRequest && this.reportResponse);
  }

  hasReportRequestContext(): boolean {
    return !!this.reportRequest;
  }

  getReportContextForLlm(): CopilotCurrentPageContext[`reportContext`] {
    return {
      title: this.reportTitle ?? undefined,
      description: this.reportDescription ?? undefined,
      request: this.toLlmReportRequestContext(this.reportRequest?.params) || {},
      response: this.toLlmReportResponseContext(this.reportResponse || undefined) || {},
      annotations: this.annotations.filter(
        (annotation) => !(annotation.tags?.length === 1 && annotation.tags?.at(0)?.name === `Mixpanel System Event`),
      ),
    };
  }

  getReportRequestContextForLlm() {
    return {
      request: this.toLlmReportRequestContext(this.reportRequest?.params) || {},
    };
  }

  initializeBoardContext(dashboardId: number, title?: string, description?: string, canEdit?: boolean) {
    this.boardContext = {
      dashboardId,
      title,
      description,
      canEdit,
      cards: {},
    };
    this.updateComponents();
  }

  setBoardCardContext(
    cardId: string,
    dashboardId: number,
    request?: BookmarkParamsUnionType,
    response?: BaseQueryResultsType | BaseQueryErrorType,
    metadata?: {title?: string; description?: string; textContent?: string},
  ) {
    if (!this.boardContext) {
      return;
    }

    // Guard against race condition: ignore updates from old board
    if (this.boardContext.dashboardId !== dashboardId) {
      return;
    }

    const existingCardContext = this.boardContext.cards[cardId];

    // Check if raw inputs have changed (prevents infinite loops)
    // We store the raw request/response references and only transform when they actually change
    if (
      existingCardContext &&
      existingCardContext._rawRequest === request &&
      existingCardContext._rawResponse === response &&
      existingCardContext.title === metadata?.title &&
      existingCardContext.description === metadata?.description &&
      existingCardContext.textContent === metadata?.textContent
    ) {
      return;
    }

    // Only transform when inputs have actually changed
    const transformedRequest = this.toLlmReportRequestContext(request);
    const transformedResponse = this.toLlmReportResponseContext(response);

    this.boardContext.cards[cardId] = {
      request: transformedRequest || {},
      response: transformedResponse || {},
      title: metadata?.title,
      description: metadata?.description,
      textContent: metadata?.textContent,
      // Store raw inputs for next comparison
      _rawRequest: request,
      _rawResponse: response,
    };

    this.updateComponents();
  }

  setBoardPlaylistCardContext(
    cardId: string,
    dashboardId: number,
    playlistData: PlaylistCardData,
    metadata?: {title?: string; description?: string},
  ) {
    if (!this.boardContext) {
      return;
    }

    if (this.boardContext.dashboardId !== dashboardId) {
      return;
    }

    const {name, filters, replayIds} = playlistData;
    const request = {
      type: `playlist` as const,
      name,
      filters,
    };
    const response = {replayIds};

    this.boardContext.cards[cardId] = {
      request,
      response,
      title: metadata?.title,
      description: metadata?.description,
    };

    this.updateComponents();
  }

  clearBoardContext() {
    this.boardContext = null;
    // Reset the per-board dedupe sets so a future board's edits/saves aren't
    // blocked by stale toolCallIds, and to keep the sets bounded across long
    // sessions.
    this.notifiedBoardEditedToolCallIds.clear();
    this.notifiedBoardSavingToolCallIds.clear();
    this.notifiedBoardSavedToolCallIds.clear();

    // Do not updateComponents during clear methods as they are triggered on component unmounts
  }

  setProfileContext(distinctId: string) {
    if (this.profileDistinctId === distinctId) {
      return;
    }
    this.clearBoardContext();
    this.clearReportContext();

    this.profileDistinctId = distinctId;
    this.updateComponents();
  }

  clearProfileContext() {
    if (!this.profileDistinctId) {
      return;
    }
    this.profileDistinctId = null;
  }

  hasProfileContext(): boolean {
    return !!this.profileDistinctId;
  }

  getProfileContextForLlm(): CopilotProfileContext | undefined {
    if (!this.hasProfileContext()) {
      return undefined;
    }
    return {distinctId: this.profileDistinctId!};
  }

  setExperimentContext(ctx: CopilotCurrentPageContext[`experimentContext`]) {
    const isNewExperiment = this.experimentCtx?.id !== ctx?.id;
    if (isNewExperiment) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearFeatureFlagContext();
    }

    // Always update the stored context so the LLM gets fresh data (e.g. when results load).
    // Only fire updateComponents on new experiment to avoid Proxy re-render loops.
    this.experimentCtx = ctx;
    if (isNewExperiment) {
      this.updateComponents();
    }
  }

  clearExperimentContext() {
    this.experimentCtx = null;
  }

  hasExperimentContext(): boolean {
    return !!this.experimentCtx;
  }

  getExperimentContextForLlm(): CopilotCurrentPageContext[`experimentContext`] | undefined {
    return this.experimentCtx ?? undefined;
  }

  setFeatureFlagContext(ctx: CopilotCurrentPageContext[`featureFlagContext`]) {
    const isNewFlag = this.featureFlagCtx?.id !== ctx?.id;
    if (isNewFlag) {
      this.clearReportContext();
      this.clearBoardContext();
      this.clearProfileContext();
      this.clearExperimentContext();
    }

    // Always update stored context so the LLM gets fresh data on setting changes.
    // Only fire updateComponents on new flag to avoid Proxy re-render loops.
    this.featureFlagCtx = ctx;
    if (isNewFlag) {
      this.updateComponents();
    }
  }

  clearFeatureFlagContext() {
    this.featureFlagCtx = null;
  }

  hasFeatureFlagContext(): boolean {
    return !!this.featureFlagCtx;
  }

  getFeatureFlagContextForLlm(): CopilotCurrentPageContext[`featureFlagContext`] | undefined {
    return this.featureFlagCtx ?? undefined;
  }

  registerSessionReplayHandler(handler: (update: SessionReplayFiltersUpdate) => void) {
    this.sessionReplayHandler = handler;
    if (this.pendingSessionReplayFiltersUpdate) {
      handler(this.pendingSessionReplayFiltersUpdate);
      this.pendingSessionReplayFiltersUpdate = null;
    }
  }

  unregisterSessionReplayHandler() {
    this.sessionReplayHandler = null;
  }

  applySessionReplayFiltersUpdate(update: SessionReplayFiltersUpdate): boolean {
    if (this.sessionReplayHandler) {
      this.sessionReplayHandler(update);
      return true;
    }
    this.pendingSessionReplayFiltersUpdate = {
      ...(this.pendingSessionReplayFiltersUpdate || {}),
      ...update,
    };
    return false;
  }

  registerReplayPlaybackHandler(handler: (req: ReplayPlaybackRequest) => void) {
    this.replayPlaybackHandler = handler;
  }

  unregisterReplayPlaybackHandler() {
    this.replayPlaybackHandler = null;
  }

  playReplayAtTimestamp(req: ReplayPlaybackRequest): boolean {
    if (this.replayPlaybackHandler) {
      this.replayPlaybackHandler(req);
      return true;
    }
    return false;
  }

  setReplayDisplayMetadata(metadataByReplayId: Map<string, ReplayDisplayMetadata>) {
    this.replayDisplayMetadata = metadataByReplayId;
  }

  getReplayDisplayMetadata(replayId: string): ReplayDisplayMetadata | undefined {
    return this.replayDisplayMetadata.get(replayId);
  }

  registerBoardEditedHandler(handler: (detail: BoardEditedDetail) => void | Promise<void>) {
    this.boardEditedHandler = handler;
  }

  unregisterBoardEditedHandler() {
    this.boardEditedHandler = null;
  }

  notifyBoardEdited(detail: BoardEditedDetail) {
    if (this.notifiedBoardEditedToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardEditedHandler) {
      return;
    }
    this.notifiedBoardEditedToolCallIds.add(detail.toolCallId);
    void this.runBoardEditedHandler(detail);
  }

  private async runBoardEditedHandler(detail: BoardEditedDetail) {
    try {
      await this.boardEditedHandler?.(detail);
    } catch (err) {
      // Drop the dedupe entry on failure so a future re-mount/retry can succeed.
      // Without this, a single transient fetch failure leaves the user looking at
      // stale data with no recovery path.
      this.notifiedBoardEditedToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardEditedHandler failed`, err);
    }
  }

  registerBoardSavingHandler(handler: (detail: BoardSaveDetail) => void | Promise<void>) {
    this.boardSavingHandler = handler;
  }

  unregisterBoardSavingHandler() {
    this.boardSavingHandler = null;
  }

  notifyBoardSaving(detail: BoardSaveDetail) {
    if (this.notifiedBoardSavingToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardSavingHandler) {
      return;
    }
    this.notifiedBoardSavingToolCallIds.add(detail.toolCallId);
    void this.runBoardSavingHandler(detail);
  }

  private async runBoardSavingHandler(detail: BoardSaveDetail) {
    try {
      await this.boardSavingHandler?.(detail);
    } catch (err) {
      this.notifiedBoardSavingToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardSavingHandler failed`, err);
    }
  }

  registerBoardSavedHandler(handler: (detail: BoardSaveDetail) => void | Promise<void>) {
    this.boardSavedHandler = handler;
  }

  unregisterBoardSavedHandler() {
    this.boardSavedHandler = null;
  }

  notifyBoardSaved(detail: BoardSaveDetail) {
    if (this.notifiedBoardSavedToolCallIds.has(detail.toolCallId)) {
      return;
    }
    if (!this.boardSavedHandler) {
      return;
    }
    this.notifiedBoardSavedToolCallIds.add(detail.toolCallId);
    void this.runBoardSavedHandler(detail);
  }

  private async runBoardSavedHandler(detail: BoardSaveDetail) {
    try {
      await this.boardSavedHandler?.(detail);
    } catch (err) {
      this.notifiedBoardSavedToolCallIds.delete(detail.toolCallId);
      // eslint-disable-next-line no-console
      console.error(`[copilot] boardSavedHandler failed`, err);
    }
  }

  setSessionReplayContext(ctx: CopilotCurrentPageContext[`sessionReplayContext`]) {
    this.sessionReplayCtx = ctx;
  }

  clearSessionReplayContext() {
    this.sessionReplayCtx = null;
  }

  setActiveReplayIds(replayIds: string[]) {
    if (!this.sessionReplayCtx) {
      return;
    }
    this.sessionReplayCtx = {
      ...this.sessionReplayCtx,
      activeReplayIds: replayIds.length ? replayIds : undefined,
    };
  }

  hasSessionReplayContext(): boolean {
    return !!this.sessionReplayCtx?.replayIds?.length;
  }

  getSessionReplayContextForLlm(): CopilotCurrentPageContext[`sessionReplayContext`] | undefined {
    return this.sessionReplayCtx ?? undefined;
  }

  private toLlmReportRequestContext(params?: BookmarkParamsUnionType): UnknownObj | undefined {
    if (!params) {
      return undefined;
    }

    if (`sections` in params) {
      const bookmark = params as InsightsBookmarkParams;
      return pick(bookmark, [`displayOptions`, `liftComparison`, `name`, `sections`, `timeComparison`]);
    }

    // Destructure to exclude visual/display properties that aren't useful for LLM analysis
    const {displayOptions, columnWidths, legend, sorting, ...semanticParams} = params as any;
    return {...semanticParams};
  }

  private toLlmReportResponseContext(response?: BaseQueryResultsType | BaseQueryErrorType): UnknownObj | undefined {
    if (!response) {
      return undefined;
    }

    // Errors are useful as-is for LLM analysis
    if (`error` in response) {
      const {error, message, status, data} = response as BaseQueryErrorType;
      return {error, message, status, data};
    }

    // For successful results, keep all data arrays and semantic fields
    // Exclude only metadata that's not useful for LLM analysis
    const {computed_at: _computedAt, meta, ...relevantData} = response as any;

    // Extract only useful metadata if present
    const filteredMeta = meta?.report_sections ? {report_sections: meta.report_sections} : undefined;

    return {
      ...relevantData,
      ...(filteredMeta ? {meta: filteredMeta} : {}),
    };
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any>) {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any>) {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  }

  generateContextBody() {
    const body: {currentPageContext: CopilotCurrentPageContext | undefined} = {
      currentPageContext: undefined,
    };

    if (this.boardContext) {
      // Filter out internal _raw* fields before sending to server
      const sanitizedCards = Object.entries(this.boardContext.cards).reduce(
        (acc, [cardId, cardContext]) => {
          const {_rawRequest, _rawResponse, ...sanitizedCardContext} = cardContext;
          acc[cardId] = sanitizedCardContext;
          return acc;
        },
        {} as Record<string, CopilotCardContext>,
      );

      body.currentPageContext = {
        boardContext: {
          dashboardId: this.boardContext.dashboardId?.toString(),
          title: this.boardContext.title,
          description: this.boardContext.description,
          canEdit: this.boardContext.canEdit,
          cards: sanitizedCards,
        },
      };
    } else if (this.hasReportContext()) {
      body.currentPageContext = {
        reportContext: this.getReportContextForLlm(),
      };
    } else if (this.hasProfileContext()) {
      body.currentPageContext = {
        profileContext: this.getProfileContextForLlm(),
      };
    } else if (this.hasExperimentContext()) {
      body.currentPageContext = {
        experimentContext: this.getExperimentContextForLlm(),
      };
    } else if (this.hasFeatureFlagContext()) {
      body.currentPageContext = {
        featureFlagContext: this.getFeatureFlagContextForLlm(),
      };
    } else if (this.hasSessionReplayContext()) {
      body.currentPageContext = {
        sessionReplayContext: this.getSessionReplayContextForLlm(),
      };
    }

    return body;
  }
}
